import { BRAND_ASSETS } from './brand';

export const SEO_BRAND_NAME = 'AbleComply';

export const PUBLIC_INDEXABLE_PATHS = [
  '/',
  '/pricing',
  '/about',
  '/resources',
  '/methodology',
  '/sample-report',
  '/how-do-i-make-my-website-ada-compliant',
  '/how-to-check-wcag-compliance',
  '/can-ai-detect-accessibility-issues',
  '/accessibility-audit-software-for-agencies',
  '/best-accessibility-audit-tools',
  '/how-to-prepare-for-eaa-compliance',
  '/accessibility-compliance-report-for-procurement',
  '/security',
  '/accessibility-statement',
  '/contact',
  '/walkthrough',
  '/privacy-policy',
  '/terms-and-conditions',
  '/cookie-policy',
] as const;

export type PublicIndexablePath = (typeof PUBLIC_INDEXABLE_PATHS)[number];
export type StructuredDataSchema = Record<string, unknown>;
export type StructuredPageType = 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage';

interface BreadcrumbItem {
  name: string;
  path: string;
}

function resolveAbsoluteUrl(path: string): string {
  if (typeof window === 'undefined') {
    return path;
  }

  return new URL(path, window.location.origin).toString();
}

export function buildOrganizationSchema(description?: string): StructuredDataSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_BRAND_NAME,
    url: resolveAbsoluteUrl('/'),
    logo: resolveAbsoluteUrl(BRAND_ASSETS.appIconPng),
    description: description ?? 'Accessibility compliance operations software for recurring monitoring, authenticated route visibility, reporting, and remediation follow-through.',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@ablecomply.com',
        url: resolveAbsoluteUrl('/contact'),
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@ablecomply.com',
        url: resolveAbsoluteUrl('/contact'),
      },
    ],
  };
}

export function buildWebsiteSchema(description: string): StructuredDataSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SEO_BRAND_NAME,
    url: resolveAbsoluteUrl('/'),
    description,
    inLanguage: typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en',
    publisher: {
      '@type': 'Organization',
      name: SEO_BRAND_NAME,
      url: resolveAbsoluteUrl('/'),
    },
  };
}

export function buildWebPageSchema(input: {
  title: string;
  description: string;
  path: string;
  pageType?: StructuredPageType;
}): StructuredDataSchema {
  return {
    '@context': 'https://schema.org',
    '@type': input.pageType ?? 'WebPage',
    name: input.title,
    description: input.description,
    url: resolveAbsoluteUrl(input.path),
    isPartOf: {
      '@type': 'WebSite',
      name: SEO_BRAND_NAME,
      url: resolveAbsoluteUrl('/'),
    },
    inLanguage: typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en',
  };
}

export function buildArticleSchema(input: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
}): StructuredDataSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.headline,
    description: input.description,
    url: resolveAbsoluteUrl(input.path),
    mainEntityOfPage: resolveAbsoluteUrl(input.path),
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: {
      '@type': 'Organization',
      name: SEO_BRAND_NAME,
      url: resolveAbsoluteUrl('/'),
    },
    publisher: {
      '@type': 'Organization',
      name: SEO_BRAND_NAME,
      url: resolveAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: resolveAbsoluteUrl(BRAND_ASSETS.appIconPng),
      },
    },
    inLanguage: typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en',
  };
}

export function buildBreadcrumbSchema(items: readonly BreadcrumbItem[]): StructuredDataSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: resolveAbsoluteUrl(item.path),
    })),
  };
}
